// Marketing copy for the proposal strategy view + PDF, transcribed from the
// "TDC Portal Interactive Presentation" doc set (objective copy matrix,
// secondary supporting bullets, good/better/best package blurbs, combined
// templates). Keyed by objective slug with a generic fallback so a new
// objective added in the DB degrades gracefully until copy lands here.
//
// Composition rules ("Why this mix works" usage logic, from the doc):
//   - headline from the primary objective
//   - two primary bullets
//   - one supporting bullet from the secondary objective
//   - a fourth bullet if space allows
//   - 3-4 bullets total, never the same content rationale twice

import { ProposalTier } from "./types";

// Shown to ANONYMOUS viewers in place of real pricing (TDC-1563). The API
// nulls dollar figures for unauthenticated callers; every surface (view page
// header, wizard step 3, PDF investment page) renders this line ONCE where
// the headline total sat, with the other price slots going neutral.
export const ANON_PRICE_TEASER = "As low as $20k for omnichannel video";

export interface WhyMixCopy {
    headline: string;
    bullets: [string, string, string];
    sellerTalkTrack: string;
    shortHeadline: string;
    shortBullets: string;
}

export interface ResultsStat {
    stat: string;
    title: string;
    source: string;
    description: string;
}

const WHY_MIX: Record<string, WhyMixCopy> = {
    "increase-conversions": {
        headline: "Built to reduce hesitation",
        bullets: [
            "Product Reviews build confidence by showing shoppers what the product is and why it fits.",
            "FAQs answer the questions that can stop shoppers from taking the next step.",
            "How-To content clarifies value by showing the product in real use.",
        ],
        sellerTalkTrack:
            "This mix is designed to remove uncertainty so more shoppers feel ready to buy.",
        shortHeadline: "Reduce hesitation",
        shortBullets: "Builds confidence. Answers objections. Shows value in use.",
    },
    "improve-retail-media-performance": {
        headline: "Content built for action",
        bullets: [
            "Product Reviews give paid placements a clearer, more credible product story.",
            "Curation helps shoppers discover relevant products through expert-guided recommendations.",
            "Together, they make media feel more useful, not just more visible.",
        ],
        sellerTalkTrack:
            "This mix helps retail media work harder by matching expert content to shopper intent.",
        shortHeadline: "Built for action",
        shortBullets:
            "Strengthens paid placements. Guides discovery. Drives more useful engagement.",
    },
    "win-ai-shopping-search": {
        headline: "Make products easier to understand",
        bullets: [
            "Product Reviews create clear product explanations that support human and AI understanding.",
            "FAQs capture the questions shoppers and AI-powered tools are likely to evaluate.",
            "The AI-ready layer turns expert content into structured product intelligence.",
        ],
        sellerTalkTrack:
            "This mix helps your product become easier to understand, retrieve, and recommend.",
        shortHeadline: "AI-ready product clarity",
        shortBullets:
            "Structures product knowledge. Answers shopper questions. Supports discoverability.",
    },
    "reduce-returns": {
        headline: "Set clearer expectations",
        bullets: [
            "FAQs address the misunderstandings that often lead to hesitation, dissatisfaction, or wrong-fit purchases.",
            "How-To content shows shoppers how to use the product before and after purchase.",
            "Clearer education helps customers buy with confidence and use the product correctly.",
        ],
        sellerTalkTrack:
            "This mix helps reduce confusion before purchase and support success after purchase.",
        shortHeadline: "Set expectations",
        shortBullets:
            "Clarifies usage. Answers concerns. Helps shoppers choose correctly.",
    },
    "build-trust-credibility": {
        headline: "Validate the decision",
        bullets: [
            "Product Reviews add credible expert perspective beyond brand claims.",
            "FAQs create transparency by answering common concerns directly and clearly.",
            "Together, they help shoppers feel more confident in the product and the brand.",
        ],
        sellerTalkTrack:
            "This mix helps build trust by giving shoppers credible answers at decision-making moments.",
        shortHeadline: "Validate the decision",
        shortBullets:
            "Adds expert perspective. Answers concerns. Builds confidence beyond brand claims.",
    },
    "educate-complex-products": {
        headline: "Turn complexity into clarity",
        bullets: [
            "Product Reviews explain what the product is, how it works, and who it is for.",
            "FAQs break down the questions shoppers need answered before they feel comfortable buying.",
            "How-To content shows the product in action so value becomes easier to understand.",
        ],
        sellerTalkTrack:
            "This mix helps shoppers understand the product faster and feel more confident choosing it.",
        shortHeadline: "Make complexity clear",
        shortBullets:
            "Explains the product. Answers questions. Demonstrates how it works.",
    },
    "launch-new-product": {
        headline: "Introduce and validate",
        bullets: [
            "Product Reviews help shoppers quickly understand what the new product is and why it matters.",
            "FAQs answer early questions that often come up with unfamiliar products.",
            "How-To content shows how the product fits into real-life use cases.",
        ],
        sellerTalkTrack:
            "This mix gives new products the education and validation they need to drive adoption.",
        shortHeadline: "Introduce and validate",
        shortBullets:
            "Explains what's new. Answers early questions. Shows real-world use.",
    },
    "help-shoppers-choose": {
        headline: "Guide the choice",
        bullets: [
            "Curation walks shoppers through the lineup and matches each option to the right buyer.",
            "FAQs answer the questions shoppers weigh when deciding between similar options.",
            "Product Reviews add credible perspective on each option's strengths and fit.",
        ],
        sellerTalkTrack:
            "This mix helps shoppers understand their options and choose with confidence.",
        shortHeadline: "Guide the choice",
        shortBullets:
            "Navigates the lineup. Answers decision questions. Builds selection confidence.",
    },
    "maximize-omnichannel-performance": {
        headline: "Connect the full journey",
        bullets: [
            "Product Reviews, FAQs, and How-Tos support shoppers from evaluation through confident purchase.",
            "Curation adds expert-guided discovery across broader campaigns, categories, or product sets.",
            "The AI-ready layer extends content value by supporting discoverability and structured understanding.",
        ],
        sellerTalkTrack:
            "This mix creates a connected content system built to support shoppers across channels.",
        shortHeadline: "Connect the journey",
        shortBullets:
            "Supports discovery. Builds confidence. Extends value across channels.",
    },
};

const GENERIC_WHY_MIX: WhyMixCopy = {
    headline: "Why this mix works",
    bullets: [
        "Expert reviews and demos validate your product and reduce hesitation.",
        "Comparison and FAQ videos answer common questions before they become barriers.",
        "Short, impactful videos designed to perform across your selected channels.",
    ],
    sellerTalkTrack:
        "This mix pairs credible expert voices with the moments that move shoppers to act.",
    shortHeadline: "Why this mix works",
    shortBullets: "Builds trust. Answers objections. Optimized for your placements.",
};

// "Use these when the objective is selected as the secondary goal."
const SECONDARY_SUPPORT_BULLETS: Record<string, string> = {
    "increase-conversions":
        "This adds conversion support by reducing hesitation and helping shoppers feel ready to buy.",
    "improve-retail-media-performance":
        "This adds media value by making placements more relevant, credible, and action-oriented.",
    "win-ai-shopping-search":
        "This adds AI readiness by making product content easier to understand and retrieve.",
    "reduce-returns":
        "This adds expectation-setting that can help shoppers choose and use the product correctly.",
    "build-trust-credibility":
        "This adds credibility by giving shoppers expert-backed information before they decide.",
    "educate-complex-products":
        "This adds clarity by breaking down product value, usage, and common questions.",
    "launch-new-product":
        "This adds launch support by introducing, explaining, and validating the product quickly.",
    "help-shoppers-choose":
        "This adds decision support by helping shoppers compare options and choose the right fit.",
    "maximize-omnichannel-performance":
        "This adds full-journey support by connecting education, discovery, conversion, and AI readiness.",
};

// Primary-objective package blurbs (Good/Better/Best doc). win-ai is retired
// from the wizard but kept here so historical proposals still render.
const PRIMARY_OBJECTIVE_BLURBS: Record<string, string> = {
    "increase-conversions":
        "This package is built to reduce hesitation at the point of decision by helping shoppers understand the product, trust the recommendation, and feel confident taking the next step.",
    "improve-retail-media-performance":
        "This package is designed to make retail media placements work harder by pairing stronger product storytelling with content that is more useful, credible, and relevant to shoppers.",
    "win-ai-shopping-search":
        "This package makes your product easier for shoppers and AI assistants to understand, retrieve, and recommend at the moments that matter.",
    "reduce-returns":
        "This package helps set clearer expectations before purchase and gives shoppers the education they need to choose, use, and feel satisfied with the product.",
    "build-trust-credibility":
        "This package gives shoppers credible expert perspective and clear answers, helping the product feel more trustworthy than brand claims alone.",
    "educate-complex-products":
        "This package breaks down product details, use cases, setup, and benefits so shoppers can understand the product faster and feel more confident evaluating it.",
    "launch-new-product":
        "This package helps introduce, explain, and validate a new product so shoppers understand what it is, why it matters, and how it fits into their lives.",
    "help-shoppers-choose":
        "This package helps shoppers navigate product lines, variants, bundles, or similar options so they can understand the differences and choose with confidence.",
    "maximize-omnichannel-performance":
        "This package creates a connected content system that supports discovery, education, conversion, and confidence across the shopper journey.",
};

// "Use one of these as the final sentence based on the selected secondary
// objective."
const SECONDARY_SUPPORT_LINES: Record<string, string> = {
    "increase-conversions":
        "It also adds conversion support by reducing friction and helping shoppers feel more ready to buy.",
    "improve-retail-media-performance":
        "It also strengthens retail media by giving placements more useful, decision-oriented content.",
    "win-ai-shopping-search":
        "It also adds AI readiness by making product content easier to understand and retrieve.",
    "reduce-returns":
        "It also helps reduce post-purchase friction by setting expectations and supporting clearer product understanding.",
    "build-trust-credibility":
        "It also reinforces trust by giving shoppers credible answers and expert-backed validation.",
    "educate-complex-products":
        "It also adds clarity by making product details, usage, and benefits easier to understand.",
    "launch-new-product":
        "It also supports launch momentum by helping shoppers quickly understand and evaluate what is new.",
    "help-shoppers-choose":
        "It also helps shoppers compare options and feel more confident selecting the right fit.",
    "maximize-omnichannel-performance":
        "It also extends the strategy across more shopper moments, channels, and decision points.",
};

// "Shorter UI Version" — compact tier cards.
export const TIER_SHORT_BLURBS: Record<ProposalTier, string> = {
    good: "A focused recommendation built around the clearest content mix for your primary objective, with targeted support for your secondary goal.",
    better: "A deeper recommendation that expands coverage across priority products, questions, use cases, or shopper decision barriers.",
    best: "A more complete content system designed to support both objectives across more products, channels, and shopper moments.",
};

const TIER_SHORT_BLURBS_NO_SECONDARY: Record<ProposalTier, string> = {
    good: "A focused recommendation built around the clearest content mix for your primary objective.",
    better: "A deeper recommendation that expands coverage across priority products, questions, use cases, or shopper decision barriers.",
    best: "A more complete content system designed to support your objective across more products, channels, and shopper moments.",
};

export const tierShortBlurb = (tier: ProposalTier, hasSecondary: boolean): string =>
    hasSecondary ? TIER_SHORT_BLURBS[tier] : TIER_SHORT_BLURBS_NO_SECONDARY[tier];

// "How this drives results" tiles (deck slide 3).
export const RESULTS_STATS: ResultsStat[] = [
    {
        stat: "4.4×",
        title: "Average Sales Lift",
        source: "Based on client conversion data",
        description: "Products featuring expert video content consistently drive more sales.",
    },
    {
        stat: "25%",
        title: "Increase in AOV",
        source: "At a major retailer",
        description: "Expert guidance gives shoppers the confidence to purchase more.",
    },
    {
        stat: "15-Point",
        title: "Conversion Lift",
        source: "For Amazon Sponsored Product Ads",
        description: "Expert content for a major CPG brand increased conversion by 15 percentage points.",
    },
    {
        stat: "Reduced",
        title: "Returns",
        source: "Through better product education",
        description: "Expert video content helps shoppers make informed purchases, reducing product returns.",
    },
];

// Left-rail "Why expert-led video?" blurb (deck slide 3).
export const WHY_EXPERT_LED =
    "Expert voices build trust, reduce hesitation, and help shoppers confidently take action.";

// Always-included Agentic AI layer — presented as a channel that's on for
// every package because every TDC video is auto-transcribed with agentic
// commerce code generated. Presentational only; never billed.
export const AGENTIC_AI_COPY = {
    title: "AI shopping / Agentic AI",
    badge: "Included with every package",
    description:
        "Every video ships AI-ready: auto-transcribed with agentic commerce code generated, so AI shopping assistants can understand, cite, and recommend your products.",
};

// Durations shown on the video-mix cards (deck slide 3), keyed by
// content_types.name.
const CONTENT_TYPE_DURATIONS: Record<string, string> = {
    review: "60–90 sec",
    how_to: "45–90 sec",
    FAQ: "30–60 sec",
    "Curation Video": "45–60 sec",
    Comparison: "60 sec",
    founder_story: "60–90 sec",
};

export const contentTypeDuration = (name?: string | null): string | null =>
    (name && CONTENT_TYPE_DURATIONS[name.trim()]) || null;

// Impact stars fall back to the content type's rank within the objective:
// rank 1 → 5 stars, rank 2 → 4, everything after → 3.
export const impactStarsForRank = (rank: number): number =>
    Math.min(5, Math.max(3, 5 - (rank - 1)));

export const whyMixCopyFor = (slug?: string | null): WhyMixCopy =>
    (slug && WHY_MIX[slug]) || GENERIC_WHY_MIX;

export const secondarySupportBullet = (slug?: string | null): string | null =>
    (slug && SECONDARY_SUPPORT_BULLETS[slug]) || null;

// Doc rule: "Do not repeat the same content rationale twice." Bullets lead
// with their rationale (Product Reviews / FAQs / How-To / Curation / the
// AI-ready layer) — dedupe on that leading token.
const RATIONALE_TOKENS = [
    "Product Reviews",
    "FAQs",
    "How-To",
    "Curation",
    "The AI-ready layer",
];
const rationaleKey = (bullet: string): string | null => {
    const hit = RATIONALE_TOKENS.find((t) =>
        bullet.toLowerCase().startsWith(t.toLowerCase()),
    );
    return hit || null;
};

export interface ComposedWhyMix {
    headline: string;
    bullets: string[];
    sellerTalkTrack: string;
}

// Usage logic from the copy library: headline + 2 bullets from the primary,
// 1 supporting bullet from the secondary, a 4th if it adds a new rationale,
// 3-4 total.
export const composeWhyThisMix = (
    primarySlug?: string | null,
    secondarySlug?: string | null,
): ComposedWhyMix => {
    const primary = whyMixCopyFor(primarySlug);
    const bullets: string[] = [];
    const seenRationales = new Set<string>();
    const push = (bullet: string | null) => {
        if (!bullet || bullets.length >= 4) return;
        const key = rationaleKey(bullet);
        if (key) {
            if (seenRationales.has(key)) return;
            seenRationales.add(key);
        }
        bullets.push(bullet);
    };

    push(primary.bullets[0]);
    push(primary.bullets[1]);
    const secondaryBullet = secondarySlug
        ? secondarySupportBullet(secondarySlug)
        : null;
    push(secondaryBullet);
    push(primary.bullets[2]);

    return {
        headline: primary.headline,
        bullets,
        sellerTalkTrack: primary.sellerTalkTrack,
    };
};

const TIER_LABELS: Record<ProposalTier, string> = {
    good: "Good",
    better: "Better",
    best: "Best",
};

// Combined blurb templates (Good/Better/Best doc): package template sentence
// + primary objective blurb + secondary support line.
export const composePackageBlurb = (
    tier: ProposalTier,
    primary: { name: string; slug: string },
    secondary?: { name: string; slug: string } | null,
): string => {
    const p = primary.name.toLowerCase();
    const s = secondary ? secondary.name.toLowerCase() : null;
    const label = TIER_LABELS[tier];

    let opener: string;
    if (tier === "good") {
        opener = s
            ? `The ${label} package focuses on the most direct content mix for ${p}, while incorporating the essential support needed for ${s}.`
            : `The ${label} package focuses on the most direct content mix for ${p}, a focused starting point for solving the highest-priority shopper challenge without overextending the scope.`;
    } else if (tier === "better") {
        opener = s
            ? `The ${label} package builds on the core recommendation with added depth across the most important content types for ${p} and ${s}.`
            : `The ${label} package builds on the core recommendation with added depth across the most important content types for ${p}.`;
    } else {
        opener = s
            ? `The ${label} package creates the most complete content system for ${p} and ${s}.`
            : `The ${label} package creates the most complete content system for ${p}.`;
    }

    const primaryBlurb = PRIMARY_OBJECTIVE_BLURBS[primary.slug] || "";
    const secondaryLine =
        (secondary && SECONDARY_SUPPORT_LINES[secondary.slug]) || "";
    return [opener, primaryBlurb, secondaryLine].filter(Boolean).join(" ");
};
